// @flow
import React, { PureComponent } from 'react';
import styled, { css } from 'styled-components';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import MoreIcon from '@atlaskit/icon/glyph/more';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Tick from '@atlaskit/icon/glyph/check-circle';
import InlineEdit from '@atlaskit/inline-edit';
import TextField from '@atlaskit/textfield';
import SectionMessage from '@atlaskit/section-message';
import { SimpleTag } from '@atlaskit/tag';
import Select from '@atlaskit/select';
import { DatePicker } from '@atlaskit/datetime-picker';
import EditorWarningIcon from '@atlaskit/icon/glyph/editor/warning';
import { Query, compose, graphql } from 'react-apollo';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import EmptyState from '@atlaskit/empty-state';
import TextArea from '@atlaskit/textarea';
import TagGroup from '@atlaskit/tag-group';
import { fontSize, gridSize } from '@atlaskit/theme';
import { isEqual } from 'lodash';
import Loading from '../virtual-components/Loading';
import ProjectVersionSteps from './step-component/ProjectVersionSteps';
import { analytics } from '../../services/analytics';
import styles from './VirtualComponentReleaseEdit.module.css';
import OctoPermissionsContext from '../../providers/OctoPermissionsContext';
import { hasPermission } from '../../utils/commons';
import { OCTO_GLOBAL_PERMISSIONS } from '../../utils/consts';
import { VIRTUAL_COMPONENT_RELEASE_WITH_JIRA_PROJECT_VERSIONS_QUERY } from '../../graphql/queries';
import { UPDATE_VIRTUAL_COMPONENT_RELEASE, DELETE_VIRTUAL_COMPONENT_RELEASE } from '../../graphql/mutations';
import { ErrorParser } from '../../utils/ErrorParser';
import VirtualComponentSelect from '../select/VirtualComponentSelect';
import VirtualComponentTabs from '../virtual-components/VirtualComponentTabs';

const Wrapper = styled.div`
    height: 100vh;
    overflow-y: auto;
    padding: 14px;
    box-sizing: border-box;
`;

const NameView = styled.h1`
    font-size: 2rem;
    font-weight: 500;
    padding-left: 6px;
    ${(props) =>
        props.error &&
        css`
            color: #f44336;
        `}
`;

const NameInput = styled.div`
    input {
        margin-bottom: 1px;
        font-size: 2rem;
        letter-spacing: -0.01em;
        font-weight: 500;
        box-sizing: border-box;
        padding: 6px;
        padding-top: 7px;
    }
`;

const DefaultView = styled.div`
    line-height: ${(gridSize() * 2.5) / fontSize()};
    padding: ${gridSize() - 2}px ${gridSize() - 2}px;
    word-break: break-word;
    cursor: text;
    white-space: pre-wrap;

    ${(props) =>
        props.isEmpty &&
        css`
            color: #bbb;
        `}
    ${(props) =>
        props.error &&
        css`
            color: #f44336;
        `}
`;

const MoreIconTrigger = (params) => {
    const { triggerRef, ...props } = params;
    return <IconButton {...props} icon={MoreIcon} label="more" ref={triggerRef} />;
};
class VirtualComponentReleaseEdit extends PureComponent {
    state = {
        newComponentVersionName: null,
        newComponentVersionIsReleased: null,
        newComponentVersionStartAt: null,
        newComponentVersionEndAt: null,
        newComponentVersionDescription: null,
        newComponentVersionComponent: null,
        newComponentVersionLinkedVersions: null,
        displayDeleteModal: false,
        componentRelease: null,
        error: '',
    };

    componentDidUpdate(prevProps) {
        if (prevProps.releaseId !== this.props.releaseId) {
            /* Reset states when user call
         other component release details */
            this.setState({
                newComponentVersionName: null,
                newComponentVersionIsReleased: null,
                newComponentVersionStartAt: null,
                newComponentVersionEndAt: null,
                newComponentVersionDescription: null,
                newComponentVersionComponent: null,
                newComponentVersionLinkedVersions: null,
                displayDeleteModal: false,
                componentRelease: null,
            });
        }
    }

    getStateValue = (defValue, stateVal) => {
        if (stateVal !== null) {
            return stateVal;
        }
        return defValue;
    };

    deleteComponentVersion() {
        this.props
            .deleteVirtualComponentRelease({
                variables: { id: this.props.releaseId },
            })
            .then(() => {
                this.props.onClose();
                this.props.onRefetchRequest();
                this.props.addFlag({
                    created: Date.now(),
                    icon: <Tick primaryColor="#00875A" />,
                    title: 'Component version deleted successfully.',
                    key: 'success',
                });
            })
            .catch(() => {
                this.props.addFlag({
                    created: Date.now(),
                    icon: <ErrorIcon primaryColor="#DE350B" />,
                    title: 'An error occurred.',
                    description: 'Component version not deleted.',
                    key: 'error',
                });
            });
    }

    ifObjectPropsChanged = (obj, obj2, key, key2) => {
        if (obj && obj2) {
            return JSON.stringify(obj[key]) !== JSON.stringify(obj2[key2 || key]);
        }
        return false;
    };

    saveChanges = (baseVirtualComponentRelease, refetch, field) => {
        if (
            !baseVirtualComponentRelease.virtualComponent &&
            ['name', 'release', 'startAt', 'endAt', 'description'].includes(field)
        ) {
            this.props.addFlag({
                created: Date.now(),
                icon: <ErrorIcon primaryColor="#DE350B" />,
                title: 'An error occurred.',
                description: "Component version can't be updated. Please assign a component to it.",
                key: 'error',
            });
            this.setState({
                newComponentVersionName: baseVirtualComponentRelease.name,
                newComponentVersionIsReleased: baseVirtualComponentRelease.isReleased,
                newComponentVersionDescription: baseVirtualComponentRelease.description,
                newComponentVersionStartAt: baseVirtualComponentRelease.startDate,
                newComponentVersionEndAt: baseVirtualComponentRelease.releaseDate,
            });
            return;
        }

        const original = { ...baseVirtualComponentRelease };
        original.selectedProjectVersions = original.selectedProjectVersions
            ? original.selectedProjectVersions.map((v) => {
                  return {
                      jiraProjectId: v.jiraProjectId,
                      jiraProjectVersionId: v.jiraProjectVersionId,
                  };
              })
            : [];

        const input = {
            id: original.id,
            component_id: original.virtualComponentId,
            name: this.getStateValue(original.name, this.state.newComponentVersionName),
            is_released: this.getStateValue(original.isReleased, this.state.newComponentVersionIsReleased),
            description: this.getStateValue(original.description, this.state.newComponentVersionDescription),
            linked_versions: original.selectedProjectVersions,
            start_at: this.getStateValue(original.startDate, this.state.newComponentVersionStartAt),
            end_at: this.getStateValue(original.releaseDate, this.state.newComponentVersionEndAt),
        };

        if (this.state.newComponentVersionComponent) {
            input.component_id = this.state.newComponentVersionComponent.value;
        }
        if (this.state.newComponentVersionLinkedVersions) {
            input.linked_versions = this.state.newComponentVersionLinkedVersions.map((v) => {
                return {
                    jiraProjectId: parseInt(v.triggerValue, 10),
                    jiraProjectVersionId: parseInt(v.value, 10),
                };
            });
        }

        this.props
            .updateVirtualComponentRelease({
                variables: {
                    input,
                },
            })
            .then((response) => {
                if (response.data.updateVirtualComponentRelease.success === false) {
                    this.setState({
                        newComponentVersionName: original.name,
                    });

                    this.props.addFlag({
                        created: Date.now(),
                        icon: <InfoIcon primaryColor="#253858" />,
                        title: 'An error occurred.',
                        description:
                            'A component version with this name already exists. Change the name and try again.',
                        key: 'warning',
                    });
                } else {
                    this.props.addFlag({
                        created: Date.now(),
                        icon: <Tick primaryColor="#00875A" />,
                        title: 'Component version updated successfully.',
                        key: 'success',
                    });

                    // Refetch component release table
                    this.props.onRefetchRequest();

                    // Refetch right side panel content
                    refetch();

                    if (this.ifObjectPropsChanged(original, input, 'name')) {
                        analytics.track({
                            userId: window.license,
                            event: 'updated_component_version',
                            timestamp: new Date(),
                            properties: {
                                addon_key: process.env.REACT_APP_KEY,
                                component_version_change_type: 'name',
                            },
                        });
                    }
                    if (this.ifObjectPropsChanged(original, input, 'description')) {
                        analytics.track({
                            userId: window.license,
                            event: 'updated_component_version',
                            timestamp: new Date(),
                            properties: {
                                addon_key: process.env.REACT_APP_KEY,
                                component_version_change_type: 'description',
                            },
                        });
                    }
                    if (this.ifObjectPropsChanged(original, input, 'startDate', 'start_at')) {
                        analytics.track({
                            userId: window.license,
                            event: 'updated_component_version',
                            timestamp: new Date(),
                            properties: {
                                addon_key: process.env.REACT_APP_KEY,
                                component_version_change_type: 'start_date',
                            },
                        });
                    }
                    if (this.ifObjectPropsChanged(original, input, 'virtualComponentId', 'component_id')) {
                        analytics.track({
                            userId: window.license,
                            event: 'updated_component_version',
                            timestamp: new Date(),
                            properties: {
                                addon_key: process.env.REACT_APP_KEY,
                                component_version_change_type: 'component',
                            },
                        });
                    }
                    if (this.ifObjectPropsChanged(original, input, 'isReleased', 'is_released')) {
                        analytics.track({
                            userId: window.license,
                            event: 'released_component_version',
                            timestamp: new Date(),
                            properties: {
                                addon_key: process.env.REACT_APP_KEY,
                                release_status: input.is_released ? 'released' : 'unreleased',
                            },
                        });
                    }
                    if (this.ifObjectPropsChanged(original, input, 'selectedProjectVersions', 'linked_versions')) {
                        analytics.track({
                            userId: window.license,
                            event: 'updated_component_version',
                            timestamp: new Date(),
                            properties: {
                                addon_key: process.env.REACT_APP_KEY,
                                component_version_change_type: 'project_releases',
                            },
                        });
                    }
                }
            })
            .catch((e) => {
                this.setState({
                    error: e.message || 'An error occurred. Component version not updated. Please try again later',
                });
            });
    };

    parseDate = (date) => {
        if (date) {
            const dateArr = date.split('-');
            if (dateArr.length === 3) {
                const dateObj = new Date();
                dateObj.setFullYear(parseInt(dateArr[0], 10));
                dateObj.setMonth(parseInt(dateArr[1], 10) - 1);
                dateObj.setDate(parseInt(dateArr[2], 10));
                dateObj.setHours(0, 0, 0, 0);
                return dateObj;
            }
        }
        return null;
    };

    validateEndDate = (date, virtualComponentRelease) => {
        const endDateTime = this.parseDate(date);
        if (endDateTime) {
            const startDateStr = this.state.newComponentVersionStartAt || virtualComponentRelease.startDate;

            if (startDateStr) {
                const startDateTime = this.parseDate(startDateStr);
                if (startDateTime) {
                    if (endDateTime.getTime() < startDateTime.getTime()) {
                        this.props.addFlag({
                            created: Date.now(),
                            icon: <ErrorIcon primaryColor="#DE350B" />,
                            title: 'An error occurred.',
                            description: 'End date cannot be less than start date.',
                            key: 'error',
                        });
                        return false;
                    }
                }
            }
        }
        return true;
    };

    validateStartDate = (date, virtualComponentRelease) => {
        const startDateTime = this.parseDate(date);
        if (startDateTime) {
            const endDateStr = this.state.newComponentVersionEndAt || virtualComponentRelease.releaseDate;

            if (endDateStr) {
                const endDateTime = this.parseDate(endDateStr);
                if (endDateTime) {
                    if (startDateTime.getTime() > endDateTime.getTime()) {
                        this.props.addFlag({
                            created: Date.now(),
                            icon: <ErrorIcon primaryColor="#DE350B" />,
                            title: 'An error occurred.',
                            description: 'Start date cannot be greater than end date.',
                            key: 'error',
                        });
                        return false;
                    }
                }
            }
        }
        return true;
    };

    getGroupVersions = (versions) => {
        const groups = [];
        if (versions) {
            groups.push({
                label: 'Unreleased',
                options: versions
                    .filter((v) => v.released === false)
                    .map((v) => {
                        return { label: v.name, value: v.id };
                    }),
            });
            groups.push({
                label: 'Released',
                options: versions
                    .filter((v) => v.released)
                    .map((v) => {
                        return { label: v.name, value: v.id };
                    }),
            });
        }
        return groups;
    };

    getSelectedJiraVersions = (release) => {
        const versions = [];
        if (release && release.selectedProjectVersions) {
            const selectedVersions = [...release.selectedProjectVersions];
            const allVersions = release.jiraProjectVersions;

            return selectedVersions
                .map((selectedVersion) => {
                    const relatedJiraVersion = allVersions.filter((av) => {
                        return (
                            av.id === selectedVersion.jiraProjectVersionId &&
                            av.projectId === selectedVersion.jiraProjectId
                        );
                    });
                    if (relatedJiraVersion.length) {
                        return relatedJiraVersion[0];
                    }
                    return null;
                })
                .filter((v) => v !== null);
        }
        return versions;
    };

    hasPermissionToEdit = (permissions) => hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions);

    render() {
        const { releaseId } = this.props;

        return (
            <OctoPermissionsContext.Consumer>
                {(permissions) => (
                    <Wrapper>
                        <Query
                            displayName="Release_Query"
                            query={VIRTUAL_COMPONENT_RELEASE_WITH_JIRA_PROJECT_VERSIONS_QUERY}
                            variables={{ id: releaseId }}
                        >
                            {({ loading: isLoading, error, data: releaseData, networkStatus, refetch }) => {
                                if (isLoading || networkStatus === 4) {
                                    return <Loading message="Loading component version..." />;
                                }
                                if (error) {
                                    return `Error! ${error.message}`;
                                }

                                const componentRelease = releaseData.virtualComponentReleaseWithJiraProjectVersions;
                                const selectedJiraProjectVersions = this.getSelectedJiraVersions(componentRelease);
                                const projectVersionsViewComponent = (
                                    <div className={styles.Description}>
                                        {this.state.newComponentVersionLinkedVersions?.length ? (
                                            <div style={{ padding: `${gridSize() / 2}px` }}>
                                                <TagGroup>
                                                    {(this.state.newComponentVersionLinkedVersions ?? []).map((v) => (
                                                        <SimpleTag text={v.label} key={v.value} />
                                                    ))}
                                                </TagGroup>
                                            </div>
                                        ) : selectedJiraProjectVersions?.length ? (
                                            <div style={{ padding: `${gridSize() / 2}px` }}>
                                                <TagGroup>
                                                    {(selectedJiraProjectVersions ?? []).map((v) => (
                                                        <SimpleTag text={v.name} key={v.id} />
                                                    ))}
                                                </TagGroup>
                                            </div>
                                        ) : (
                                            <DefaultView isEmpty>No project versions selected</DefaultView>
                                        )}
                                    </div>
                                );

                                if (!componentRelease) {
                                    return (
                                        <div>
                                            <div className={styles.HeaderNav}>
                                                <Button
                                                    appearance="subtle"
                                                    iconBefore={<CrossIcon />}
                                                    onClick={this.props.onClose}
                                                />
                                            </div>
                                            <EmptyState
                                                header="Something went wrong"
                                                description="Unfortunately, detailed information for this component release is no longer available."
                                            />
                                        </div>
                                    );
                                }

                                return (
                                    <div>
                                        <div className={styles.HeaderNav}>
                                            {hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions) && (
                                                <DropdownMenu trigger={MoreIconTrigger} position="bottom right">
                                                    <DropdownItemGroup>
                                                        <DropdownItem
                                                            onClick={() => {
                                                                this.setState({
                                                                    displayDeleteModal: true,
                                                                    componentRelease,
                                                                });
                                                            }}
                                                        >
                                                            Delete
                                                        </DropdownItem>
                                                    </DropdownItemGroup>
                                                </DropdownMenu>
                                            )}
                                            <Button
                                                appearance="subtle"
                                                iconBefore={<CrossIcon />}
                                                onClick={this.props.onClose}
                                            ></Button>
                                        </div>
                                        {this.state.error && (
                                            <SectionMessage appearance="error">
                                                <p>There was an error editing the component version:</p>
                                                <p style={{ whiteSpace: 'pre-wrap' }}>
                                                    {ErrorParser.parse(this.state.error)}
                                                </p>
                                            </SectionMessage>
                                        )}

                                        {/* name */}
                                        <InlineEdit
                                            Label="Name"
                                            defaultValue={this.state.newComponentVersionName || componentRelease.name}
                                            readViewFitContainerWidth
                                            isEditing={this.hasPermissionToEdit(permissions) ? undefined : false}
                                            editView={(fieldProps) => (
                                                <NameInput>
                                                    <TextField {...fieldProps} autoFocus />
                                                </NameInput>
                                            )}
                                            readView={() => {
                                                const theName =
                                                    this.state.newComponentVersionName || componentRelease.name;
                                                return (
                                                    <NameView error={!theName}>
                                                        {theName || 'Insert a name for this component version'}
                                                    </NameView>
                                                );
                                            }}
                                            onConfirm={(value) => {
                                                if (!value || componentRelease.name === value) return;
                                                this.setState(
                                                    {
                                                        newComponentVersionName: value,
                                                    },
                                                    this.saveChanges.bind(this, componentRelease, refetch, 'name'),
                                                );
                                            }}
                                        />

                                        {/* Status */}
                                        <InlineEdit
                                            label="Status"
                                            defaultValue={
                                                this.state.newComponentVersionIsReleased || componentRelease.isReleased
                                                    ? { label: 'Released', value: 1 }
                                                    : { label: 'Unreleased', value: 0 }
                                            }
                                            readViewFitContainerWidth
                                            hideActionButtons={
                                                !hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions)
                                            }
                                            isEditing={this.hasPermissionToEdit(permissions) ? undefined : false}
                                            editView={(fieldProps) => (
                                                <Select
                                                    autoFocus
                                                    {...fieldProps}
                                                    options={[
                                                        { label: 'Released', value: 1 },
                                                        { label: 'Unreleased', value: 0 },
                                                    ]}
                                                    placeholder="Choose status"
                                                    isDisabled={
                                                        !hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions)
                                                    }
                                                />
                                            )}
                                            readView={() => {
                                                const isComponentReleased =
                                                    this.state.newComponentVersionIsReleased ||
                                                    componentRelease.isReleased;
                                                return isComponentReleased ? (
                                                    <SimpleTag text="Released" color="greenLight" />
                                                ) : (
                                                    <SimpleTag text="Unreleased" color="blueLight" />
                                                );
                                            }}
                                            onConfirm={(value) => {
                                                const newComponentVersionIsReleased = value.value === 1;
                                                if (componentRelease.isReleased === newComponentVersionIsReleased)
                                                    return;

                                                this.setState(
                                                    {
                                                        newComponentVersionIsReleased,
                                                    },
                                                    this.saveChanges.bind(this, componentRelease, refetch, 'release'),
                                                );
                                            }}
                                        />

                                        <div className={styles.Fields_Split}>
                                            <div>
                                                <div className={styles.Fields}>
                                                    <h4>Start date</h4>
                                                    <DatePicker
                                                        id="start_date_picker"
                                                        value={
                                                            this.state.newComponentVersionStartAt ||
                                                            componentRelease.startDate ||
                                                            ''
                                                        }
                                                        placeholder="eg. 07/22/2020"
                                                        isDisabled={
                                                            !hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions)
                                                        }
                                                        onChange={(value) => {
                                                            if (
                                                                value !== componentRelease.startDate &&
                                                                this.validateStartDate(value, componentRelease)
                                                            ) {
                                                                this.setState(
                                                                    {
                                                                        newComponentVersionStartAt: value,
                                                                    },
                                                                    this.saveChanges.bind(
                                                                        this,
                                                                        componentRelease,
                                                                        refetch,
                                                                        'startAt',
                                                                    ),
                                                                );
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <div className={styles.Fields}>
                                                    <h4>Release date</h4>
                                                    <DatePicker
                                                        id="release_date_picker"
                                                        value={
                                                            this.state.newComponentVersionEndAt ||
                                                            componentRelease.releaseDate ||
                                                            ''
                                                        }
                                                        placeholder="eg. 07/22/2020"
                                                        isDisabled={
                                                            !hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions)
                                                        }
                                                        onChange={(value) => {
                                                            if (
                                                                value !== componentRelease.releaseDate &&
                                                                this.validateEndDate(value, componentRelease)
                                                            ) {
                                                                this.setState(
                                                                    {
                                                                        newComponentVersionEndAt: value,
                                                                    },
                                                                    this.saveChanges.bind(
                                                                        this,
                                                                        componentRelease,
                                                                        refetch,
                                                                        'endAt',
                                                                    ),
                                                                );
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <InlineEdit
                                            label="Description"
                                            defaultValue={
                                                this.state.newComponentVersionDescription ||
                                                componentRelease.description ||
                                                ''
                                            }
                                            readViewFitContainerWidth
                                            isEditing={this.hasPermissionToEdit(permissions) ? undefined : false}
                                            editView={(fieldProps, ref) => (
                                                <TextArea ref={ref} {...fieldProps} autoFocus />
                                            )}
                                            readView={() => (
                                                <div className={styles.Description}>
                                                    {this.state.newComponentVersionDescription ||
                                                        componentRelease.description || <span>Empty description</span>}
                                                </div>
                                            )}
                                            onConfirm={(value) => {
                                                if ((value ?? '') === (componentRelease.description ?? '')) return;
                                                this.setState(
                                                    {
                                                        newComponentVersionDescription: value,
                                                    },
                                                    this.saveChanges.bind(
                                                        this,
                                                        componentRelease,
                                                        refetch,
                                                        'description',
                                                    ),
                                                );
                                            }}
                                        />

                                        <InlineEdit
                                            label="Component"
                                            defaultValue={
                                                this.state.newComponentVersionComponent || {
                                                    label: componentRelease.virtualComponent
                                                        ? componentRelease.virtualComponent.name
                                                        : '',
                                                    value: componentRelease.virtualComponentId,
                                                }
                                            }
                                            readViewFitContainerWidth
                                            hideActionButtons={
                                                !hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions)
                                            }
                                            isEditing={this.hasPermissionToEdit(permissions) ? undefined : false}
                                            editView={(fieldProps) => (
                                                <VirtualComponentSelect
                                                    {...fieldProps}
                                                    autoFocus
                                                    openMenuOnFocus
                                                    placeholder="Select component"
                                                    defaultOptions
                                                />
                                            )}
                                            readView={() => {
                                                if (this.state.newComponentVersionComponent) {
                                                    return (
                                                        <div className={styles.Description}>
                                                            {this.state.newComponentVersionComponent.label}
                                                        </div>
                                                    );
                                                }
                                                if (componentRelease.virtualComponent) {
                                                    return (
                                                        <div className={styles.Description}>
                                                            {componentRelease.virtualComponent.name}
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            color: '#FF4411',
                                                        }}
                                                    >
                                                        <EditorWarningIcon color="#FF4411" />
                                                        <span style={{ marginLeft: '5px' }}>Assign a component</span>
                                                    </div>
                                                );
                                            }}
                                            onConfirm={(value) => {
                                                if (value.value === componentRelease.virtualComponentId) return;
                                                this.setState(
                                                    {
                                                        newComponentVersionComponent: value,
                                                    },
                                                    this.saveChanges.bind(this, componentRelease, refetch),
                                                );
                                            }}
                                        />

                                        <InlineEdit
                                            label="Project versions"
                                            keepEditViewOpenOnBlur={true}
                                            defaultValue={''}
                                            readViewFitContainerWidth
                                            hideActionButtons={
                                                !hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions)
                                            }
                                            isEditing={this.hasPermissionToEdit(permissions) ? undefined : false}
                                            editView={(fieldProps) => {
                                                if (hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions)) {
                                                    return (
                                                        <ProjectVersionSteps
                                                            {...fieldProps}
                                                            value={
                                                                this.state.newComponentVersionLinkedVersions ||
                                                                selectedJiraProjectVersions.map((v) => {
                                                                    return {
                                                                        label: v.name,
                                                                        value: v.id,
                                                                        triggerValue: v.projectId,
                                                                    };
                                                                })
                                                            }
                                                        />
                                                    );
                                                }
                                                return projectVersionsViewComponent;
                                            }}
                                            readView={() => {
                                                return projectVersionsViewComponent;
                                            }}
                                            onConfirm={(value) => {
                                                if (
                                                    !value ||
                                                    isEqual(
                                                        value.map((v) => v.value),
                                                        (componentRelease?.selectedProjectVersions ?? []).map(
                                                            ({ jiraProjectVersionId }) => jiraProjectVersionId,
                                                        ),
                                                    )
                                                )
                                                    return;
                                                this.setState(
                                                    {
                                                        newComponentVersionLinkedVersions: value,
                                                    },
                                                    this.saveChanges.bind(this, componentRelease, refetch),
                                                );
                                            }}
                                        />
                                        <br />
                                        <VirtualComponentTabs
                                            type="release"
                                            virtualComponentOrVersion={componentRelease}
                                        />
                                    </div>
                                );
                            }}
                        </Query>
                        {this.state.displayDeleteModal && (
                            <ModalTransition>
                                <Modal
                                    key="delete-version-modal"
                                    appearance="warning"
                                    onClose={() => {
                                        this.setState({
                                            displayDeleteModal: false,
                                        });
                                    }}
                                >
                                    <ModalHeader>
                                        <ModalTitle>Delete {this.state.componentRelease.name}</ModalTitle>
                                    </ModalHeader>
                                    <ModalBody>Are you sure you want to delete this component version?</ModalBody>
                                    <ModalFooter>
                                        <Button
                                            appearance="warning"
                                            id="delete"
                                            onClick={() => this.deleteComponentVersion()}
                                        >
                                            Yes, delete
                                        </Button>
                                        <Button
                                            id="cancel"
                                            onClick={() =>
                                                this.setState({
                                                    displayDeleteModal: false,
                                                })
                                            }
                                        >
                                            Cancel
                                        </Button>
                                    </ModalFooter>
                                </Modal>
                            </ModalTransition>
                        )}
                    </Wrapper>
                )}
            </OctoPermissionsContext.Consumer>
        );
    }
}

export default compose(
    graphql(UPDATE_VIRTUAL_COMPONENT_RELEASE, { name: 'updateVirtualComponentRelease' }),
    graphql(DELETE_VIRTUAL_COMPONENT_RELEASE, { name: 'deleteVirtualComponentRelease' }),
)(VirtualComponentReleaseEdit);
